import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Plus, Lifebuoy } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONES = {
  open: "warning",
  in_progress: "info",
  waiting_on_user: "muted",
  resolved: "success",
  closed: "muted",
  escalated: "danger",
};

const CATEGORIES = [
  ["login_password", "Login / Password"],
  ["parent_app", "Parent App"],
  ["qr_scanner", "QR Scanner"],
  ["lms_content", "LMS / Content"],
  ["photo_video_upload", "Photo/Video Upload"],
  ["device_browser", "Device / Browser"],
  ["internet_network", "Internet / Network"],
  ["smart_board", "Smart Board"],
  ["live_test_site", "Live / Test Site"],
  ["other", "Other"],
];

const SEVERITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["open", "in_progress", "waiting_on_user", "resolved", "closed", "escalated"];

export default function TechSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/support-tickets", { params });
      setTickets(r.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load tickets");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter]);

  const visibleTickets = tickets.filter((t) =>
    !filter ||
    t.title?.toLowerCase().includes(filter.toLowerCase()) ||
    t.description?.toLowerCase().includes(filter.toLowerCase()) ||
    t.ticket_id?.toLowerCase().includes(filter.toLowerCase()),
  );

  const canUpdate =
    user?.role === "super_admin" ||
    user?.role === "franchise_hq_admin" ||
    user?.role === "hq_admin" ||
    user?.role === "branch_admin" ||
    (user?.role === "non_teaching_staff" &&
      user?.sub_role === "technical_coordinator" &&
      (user.permissions || []).includes("support_ticket_update"));

  return (
    <div data-testid="tech-support-page">
      <PageHeader
        eyebrow="Technical Support"
        title="Support tickets"
        subtitle="Track issues raised by parents, teachers and admins. Update status, add notes, and escalate when needed."
        action={
          <Button
            onClick={() => setShowNew(true)}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
            data-testid="new-ticket-btn"
          >
            <Plus size={16} /> New ticket
          </Button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="ticket-filters">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search tickets…"
          className="p-2 rounded-lg border border-border bg-paper text-sm flex-1 min-w-[200px]"
          data-testid="ticket-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-2 rounded-lg border border-border bg-paper text-sm"
          data-testid="status-filter"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <Panel>
        {loading ? (
          <div className="py-12 text-center text-ink-soft text-sm">Loading…</div>
        ) : visibleTickets.length === 0 ? (
          <div className="py-12 text-center text-ink-soft text-sm">
            No tickets to show.
          </div>
        ) : (
          <div className="space-y-2" data-testid="tickets-list">
            {visibleTickets.map((t) => (
              <button
                key={t.ticket_id}
                onClick={() => setSelected(t)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-paper hover:bg-paper-alt border border-border transition text-left"
                data-testid={`ticket-${t.ticket_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-xs text-ink-soft truncate">
                    #{t.ticket_id?.slice(-6)} · {t.category} ·{" "}
                    {(t.severity || "").toUpperCase()} ·{" "}
                    by {t.created_by_name || "—"}
                  </div>
                </div>
                <Pill tone={STATUS_TONES[t.status] || "muted"}>{t.status}</Pill>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {showNew && (
        <NewTicketModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}

      {selected && (
        <TicketDetailModal
          ticket={selected}
          canUpdate={canUpdate}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}


function NewTicketModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [severity, setSeverity] = useState("medium");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!title.trim() || !description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support-tickets", { title, description, category, severity });
      toast.success("Ticket created");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-floating" onClick={(e) => e.stopPropagation()} data-testid="new-ticket-modal">
        <div className="text-lg font-heading font-bold mb-4">
          <Lifebuoy size={20} className="inline mr-1" /> New support ticket
        </div>
        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-ticket-title" />

        <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Steps to reproduce, device, browser, screenshots if any" className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-ticket-description" />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-ticket-category">
              {CATEGORIES.map(([k, label]) => (<option key={k} value={k}>{label}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Severity</label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-ticket-severity">
              {SEVERITIES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="new-ticket-submit">{submitting ? "Creating…" : "Create"}</Button>
        </div>
      </div>
    </div>
  );
}


function TicketDetailModal({ ticket, canUpdate, onClose, onChanged }) {
  const [status, setStatus] = useState(ticket.status);
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState(ticket.resolution || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const patch = {};
      if (status !== ticket.status) patch.status = status;
      if (note.trim()) patch.note = note.trim();
      if (resolution.trim() && resolution !== ticket.resolution) patch.resolution = resolution.trim();
      if (Object.keys(patch).length === 0) {
        toast.error("No changes to save");
        setSaving(false);
        return;
      }
      await api.patch(`/support-tickets/${ticket.ticket_id}`, patch);
      toast.success("Ticket updated");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-floating max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail-modal">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="text-lg font-heading font-bold">{ticket.title}</div>
            <div className="text-xs text-ink-soft">
              #{ticket.ticket_id?.slice(-6)} · {ticket.category} ·{" "}
              {(ticket.severity || "").toUpperCase()} · by {ticket.created_by_name || "—"}
            </div>
          </div>
          <Pill tone={STATUS_TONES[ticket.status] || "muted"}>{ticket.status}</Pill>
        </div>

        <div className="bg-paper-alt rounded-lg p-3 text-sm text-ink whitespace-pre-wrap mb-4" data-testid="ticket-description">
          {ticket.description}
        </div>

        {ticket.history && ticket.history.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">History</div>
            <div className="space-y-1.5 text-xs">
              {ticket.history.slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-2">
                  <div className="text-ink-soft min-w-[110px]">{(h.ts || "").slice(0, 16).replace("T", " ")}</div>
                  <div>
                    <span className="font-semibold">{h.by_name || h.by}</span>{" "}
                    <span className="text-ink-soft">{h.event}</span>{" "}
                    {h.note && <span className="italic">— {h.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {canUpdate && (
          <div className="border-t border-border pt-4 space-y-3" data-testid="ticket-update-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="ticket-status-select">
                  {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Resolution (final)</label>
                <input value={resolution} onChange={(e) => setResolution(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" placeholder="What was done?" data-testid="ticket-resolution-input" />
              </div>
            </div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Add a note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Comment or status update" className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="ticket-note-input" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
              <Button onClick={save} disabled={saving} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="ticket-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        )}

        {!canUpdate && (
          <div className="flex justify-end pt-3 border-t border-border">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}
